interface SpeakingState {
  isSpeaking: boolean;
  volumeLevel: number;
}

interface SpeakingSnapshot {
  state: SpeakingState;
}

type SpeakingStateChangeListener = () => void;

const DEFAULT_SPEAKING_STATE: SpeakingState = {
  isSpeaking: false,
  volumeLevel: 0,
};

const clampVolumeLevel = (volumeLevel: number) => {
  if (Number.isNaN(volumeLevel)) {
    return 0;
  }
  if (volumeLevel < 0) {
    return 0;
  }
  if (volumeLevel > 1) {
    return 1;
  }
  return volumeLevel;
};

const createSnapshot = (state: SpeakingState): SpeakingSnapshot => ({ state });

const isSameSpeakingState = (
  leftState: SpeakingState,
  rightState: SpeakingState
) =>
  leftState.isSpeaking === rightState.isSpeaking &&
  leftState.volumeLevel === rightState.volumeLevel;

let currentDefaultSnapshot = createSnapshot(DEFAULT_SPEAKING_STATE);
let speakingStateByUserId = new Map<string, SpeakingState>();
let snapshotByUserId = new Map<string, SpeakingSnapshot>();
const listenersByUserId = new Map<string, Set<SpeakingStateChangeListener>>();

const getListenerSet = (userId: string) => {
  const existingListeners = listenersByUserId.get(userId);
  if (existingListeners) {
    return existingListeners;
  }

  const nextListeners = new Set<SpeakingStateChangeListener>();
  listenersByUserId.set(userId, nextListeners);
  return nextListeners;
};

const notifyUser = (userId: string) => {
  const listeners = listenersByUserId.get(userId);
  if (!listeners) {
    return;
  }

  for (const listener of listeners) {
    listener();
  }
};

const notifyAllUsers = () => {
  for (const userId of listenersByUserId.keys()) {
    notifyUser(userId);
  }
};

const setSnapshotForUser = (userId: string, state: SpeakingState) => {
  const nextSnapshot = createSnapshot(state);
  speakingStateByUserId.set(userId, state);
  snapshotByUserId.set(userId, nextSnapshot);
};

const removeSnapshotForUser = (userId: string) => {
  speakingStateByUserId.delete(userId);
  snapshotByUserId.delete(userId);
};

export type { SpeakingSnapshot, SpeakingState };

export const speakingStore = {
  clear() {
    speakingStore.reset();
  },

  getSnapshot(userId: string): SpeakingSnapshot {
    return snapshotByUserId.get(userId) ?? currentDefaultSnapshot;
  },

  getState(userId: string): SpeakingState {
    return speakingStore.getSnapshot(userId).state;
  },

  remove(userId: string) {
    const hasStoredState =
      speakingStateByUserId.has(userId) || snapshotByUserId.has(userId);
    if (!hasStoredState) {
      return;
    }

    removeSnapshotForUser(userId);
    notifyUser(userId);
  },

  reset() {
    speakingStateByUserId = new Map();
    snapshotByUserId = new Map();
    currentDefaultSnapshot = createSnapshot(DEFAULT_SPEAKING_STATE);
    notifyAllUsers();
  },
  setSpeakingState(
    userId: string,
    {
      isSpeaking,
      volumeLevel,
    }: {
      isSpeaking: boolean;
      volumeLevel: number;
    }
  ) {
    const normalizedState = {
      isSpeaking,
      volumeLevel: clampVolumeLevel(volumeLevel),
    };
    const currentState = speakingStateByUserId.get(userId);

    if (currentState) {
      if (isSameSpeakingState(currentState, normalizedState)) {
        return;
      }

      if (!normalizedState.isSpeaking && normalizedState.volumeLevel === 0) {
        removeSnapshotForUser(userId);
        notifyUser(userId);
        return;
      }
    } else if (
      !normalizedState.isSpeaking &&
      normalizedState.volumeLevel === 0
    ) {
      return;
    }

    setSnapshotForUser(userId, normalizedState);
    notifyUser(userId);
  },

  subscribe(userId: string, listener: SpeakingStateChangeListener): () => void {
    const listeners = getListenerSet(userId);
    listeners.add(listener);

    return () => {
      const currentListeners = listenersByUserId.get(userId);
      if (!currentListeners) {
        return;
      }

      currentListeners.delete(listener);
      if (currentListeners.size === 0) {
        listenersByUserId.delete(userId);
      }
    };
  },
};
